import {
  Permission,
  type App,
  type ChainInfo,
  type NetworkInfo,
} from "@alch/dx-entities";
import { Link, Tooltip } from "@alch/ui";
import NetworkEmblem from "@components/ui/icons/NetworkEmblem";
import { useUpdateAppNetworkAllowlist } from "@features/app-details-multi-network/networks/useUpdateAppNetworkAllowlist";
import useHasPermission from "@queries/useHasPermission";
import networkNameWithoutChain from "@util/networkNameWithoutChain";
import clsx from "clsx";

interface NetworkItemProps {
  networkInfo: NetworkInfo;
  chainInfo?: ChainInfo;
  app?: App;
  disabled?: boolean;
  disabledInfo?: string;
}

interface EnableNetworkActionProps {
  app: App;
  networkInfo: NetworkInfo;
}

const EnableNetworkAction = ({
  app,
  networkInfo,
}: EnableNetworkActionProps) => {
  const mutation = useUpdateAppNetworkAllowlist({ scopeId: app.sid });
  const canEditAppConfig = useHasPermission(Permission.EditAppConfig);

  return (
    <Tooltip
      content="You don't have permission to do this"
      disabled={canEditAppConfig}
      withArrow
    >
      <Link
        size="xs"
        disabled={!canEditAppConfig || mutation.isPending}
        onClick={() => {
          if (!canEditAppConfig) return;
          mutation.mutate({
            appId: app.sid,
            networkIds: [...app.networkAllowlist, networkInfo.id],
          });
        }}
      >
        Enable
      </Link>
    </Tooltip>
  );
};

const NetworkItem = ({
  networkInfo,
  chainInfo,
  disabled,
  disabledInfo,
  app,
}: NetworkItemProps) => {
  // Show the network name without chain name if optional chainInfo is provided
  const networkName = chainInfo
    ? networkNameWithoutChain({ networkInfo, chainInfo })
    : networkInfo.name;

  return (
    <div className="flex w-full items-center gap-4">
      <div
        className={clsx(
          "flex items-center gap-2",
          disabled ? "opacity-50" : undefined,
        )}
      >
        <NetworkEmblem networkInfo={networkInfo} className="size-5" />

        <span className="text-secondary">{networkName}</span>
      </div>

      {app && disabled ? (
        <EnableNetworkAction app={app} networkInfo={networkInfo} />
      ) : disabled && disabledInfo ? (
        <span className="ml-auto text-paragraph-100-medium text-tertiary">
          {disabledInfo}
        </span>
      ) : null}
    </div>
  );
};

export default NetworkItem;
